'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Cancel01Icon, Remove01Icon, Tick01Icon } from '@terra/ui-icons';
import { BodyDefault, BodyStrong } from '../../../components/typography';
import {
  buildInternalAccessDisplayChecks,
  type InternalAccessCheckStatus,
  type InternalAccessTestResult,
} from './internal-access.model';

export type InternalAccessTestResultsProps = {
  result: InternalAccessTestResult;
};

const STATUS_COLOR = {
  pass: 'fg.neutral.success',
  fail: 'fg.error',
  skip: 'fg.muted',
} as const satisfies Record<InternalAccessCheckStatus, string>;

function CheckStatusIcon({ status }: { status: InternalAccessCheckStatus }) {
  return (
    <Box as="span" display="inline-flex" color={STATUS_COLOR[status]}>
      {status === 'pass' ? (
        <Tick01Icon size={14} />
      ) : status === 'fail' ? (
        <Cancel01Icon size={14} />
      ) : (
        <Remove01Icon size={14} />
      )}
    </Box>
  );
}

export function InternalAccessTestResults({
  result,
}: InternalAccessTestResultsProps) {
  const t = useTranslations(
    'designSystem.applicationSettings.networking.internalAccess.checks',
  );
  const displayChecks = useMemo(
    () => buildInternalAccessDisplayChecks(result.checks),
    [result.checks],
  );

  if (displayChecks.length === 0) {
    return null;
  }

  return (
    <VStack gap={2} alignItems="stretch" w="full">
      {displayChecks.map((check) => (
        <HStack key={check.id} gap={2} alignItems="start">
          <CheckStatusIcon status={check.status} />
          <VStack gap={0} alignItems="start">
            <BodyStrong>{t(check.labelKey)}</BodyStrong>
            {check.detail ? (
              <BodyDefault color="fg.muted">{check.detail}</BodyDefault>
            ) : null}
          </VStack>
        </HStack>
      ))}
    </VStack>
  );
}
